import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import DictationButton from "../../../components/DictationButton";

interface AssistantChatInputProps {
  disabled: boolean;
  onSend: (text: string) => void;
}

/** Message box with dictation: type, or tap the mic and speak — then send. */
export function AssistantChatInput({ disabled, onSend }: AssistantChatInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");

  const canSend = !disabled && text.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    onSend(text.trim());
    setText("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex items-end gap-2">
      <div className="relative flex-1">
        <textarea
          className="input w-full min-h-[52px] max-h-40 resize-none pr-10"
          rows={1}
          placeholder={t("assistant.inputPlaceholder")}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label={t("assistant.inputPlaceholder")}
        />
        <span className="absolute bottom-2.5 right-1.5">
          <DictationButton
            compact
            onText={(spoken) => setText((prev) => (prev ? `${prev} ${spoken}` : spoken))}
            disabled={disabled}
          />
        </span>
      </div>
      <button
        type="button"
        className="btn btn-primary shrink-0"
        onClick={handleSend}
        disabled={!canSend}
        aria-label={t("assistant.send")}
      >
        <Send size={18} strokeWidth={2} />
        <span className="hidden sm:inline">{t("assistant.send")}</span>
      </button>
    </div>
  );
}
